import { Utils } from '../core/utils';

export interface Text {
  text: Array<TextChunk>;
  horizontalAlignment: TextAlignment;
  verticalAlignment: TextAlignment;
  fontSize: TextSize;
  fontName: string;
  color: TextColor;
  leading: LeadingFactor;
  availableArea: TextArea;
  fontDescription?: any;
}

export function updateText(model: Text, text: Array<TextChunk>): Text
{
  return {
    ...model,
    text: text,
  };
}

export interface FontDescription
{
  name: string;
  url: string;
  unicodeRanges: Array<Array<number>>;
  charDimensions: CharDimensions;
}

export interface CharDimensions
{
  baseEM: number;
  maxBearing: BearingInformation;
  widths: {[unicode: string]: GlyphConfig};
  minLeading: LeadingFactor;
}

export interface BearingInformation
{
  left: number;
  right: number;
}

export interface GlyphConfig
{
  width: number;
  kerning?: {[glyph: string]: number};
}

export interface FontBearingDescription {
  left: number;
  right: number;
}

export type TextColor = string;

export enum TextAlignment {
  Start,
  Middle,
  End,
}

// NOTE TextSize must always have the same unit.
//      By convention this unit is 'pt'.
export type TextSize = number;

// NOTE LeadingFactor is unitless and always refers to the current line height.
export type LeadingFactor = number;

export interface TextArea {
  width: TextSize;
  height: TextSize;
}

export interface TextChunk {
  kind: TextChunkKind;
  content?: string;
  breaksLine?: boolean;
  breaksWord?: boolean;
  isUserInput?: boolean;
  selection?: TextSelectionKind;
}

function chunksAreEqual(a: TextChunk, b: TextChunk): boolean
{
  return (
    (a.kind === b.kind) &&
    (a.content === b.content) &&
    (a.breaksLine === b.breaksLine) &&
    (a.breaksWord === b.breaksWord) &&
    (a.isUserInput === b.isUserInput) &&
    (a.selection === b.selection)
  );
}

export function textsAreEqual(a: Array<TextChunk>, b: Array<TextChunk>): boolean
{
  return (a.length === b.length) &&
    Utils.zip(a, b).every(it => chunksAreEqual(it[0], it[1]));
}

export enum TextChunkKind {
  Word,
  Whitespace,
  Control,
}

export enum TextSelectionKind {
  Focus,
  Anchor
}

export function scaleModel(model: Text, factor: number): Text
{
  return {
    ...model,
    fontSize: model.fontSize * factor,
    availableArea: {
      width: model.availableArea.width * factor,
      height: model.availableArea.height * factor,
    }
  };
}

export function scaledBearing(
  font: FontDescription,
  model: Text): FontBearingDescription
{
  const factor = -1 * model.fontSize / font.charDimensions.baseEM;

  return {
    left: font.charDimensions.maxBearing.left * factor,
    right: font.charDimensions.maxBearing.right * factor,
  };
}

export function normalizedTextChunk(chunk: TextChunk): TextChunk
{
  const withDefault = (v: any | undefined, defaultValue: any) =>
      v === undefined ? defaultValue : v;

  return {
    kind: chunk.kind,
    content: withDefault(chunk.content, ''),
    breaksLine: withDefault(chunk.breaksLine, false),
    breaksWord: withDefault(chunk.breaksWord, true),
    isUserInput: withDefault(chunk.isUserInput, true),
  };
}

export function normalizedFontFamily(family: string): string
{
  const unquoted = family.replace(/['"]/g, '');
  return `"${unquoted}"`;
}

export function selectCompleteText(model: Text): Text
{
  return {
    ...model,
    text: [
      { kind: TextChunkKind.Control,
        selection: TextSelectionKind.Anchor
      },
      ...(model.text.filter(c => c.kind !== TextChunkKind.Control)),
      { kind: TextChunkKind.Control,
        selection: TextSelectionKind.Focus
      }
    ]
  };
}

export function withoutFontDescription(model: Text): Text
{
  return {...model, fontDescription: undefined};
}

export function extractChunksFromLine(line: string): Array<TextChunk>
{
  const isWhitespace = /^[\s]$/;
  const chunks: Array<TextChunk> = [];
  let currentWord = '';

  const pushCurrenthWord = () => {
    if (currentWord !== '') {
      chunks.push({
        kind: TextChunkKind.Word,
        content: currentWord,
      });
      currentWord = '';
    }
  };

  for (const c of line) {
    if (isWhitespace.test(c)) {
      pushCurrenthWord();

      if (c !== '\n') {
        chunks.push({
          kind: TextChunkKind.Whitespace,
          content: c,
        });
      }
    } else {
      currentWord += c;
    }
  }
  pushCurrenthWord();

  return chunks;
}

export function plaintextToChunks(plainText: string): Array<TextChunk>
{
  const breakLine = (line, idx, arr) =>
    idx < arr.length - 1
      ? line.concat([
        { kind: TextChunkKind.Whitespace,
          breaksLine: true,
        }])
      : line;

  return Utils.flattenArray(
    plainText
      .split(/\r?\n/)
      .map(extractChunksFromLine)
      .map(breakLine)
  ).map(normalizedTextChunk);
}

interface ChunkSplitInfo
{
  before: Array<TextChunk>;
  after: Array<TextChunk>;
  cut?: Array<TextChunk>;
}

function splitChunks(chunks: Array<TextChunk>): ChunkSplitInfo | undefined
{
  const groups = chunks
    .reduce(Utils.groupByCondition((grp, c) => c.selection === undefined), [])
    .map(cs => cs.filter(c => c.selection === undefined));

  switch (groups.length) {
    case 0: return { before: [], after: [] };
    case 1: return { before: chunks, after: [] };
    case 2: return { before: groups[0], after: groups[1] };
    case 3: return { before: groups[0], after: groups[2], cut: groups[1] };
    default: return undefined;
  }
}

export function insertChunks(
  chunks: Array<TextChunk>, insert: Array<TextChunk>): Array<TextChunk>
{
  const split = splitChunks(chunks);

  if (! split) {
    return chunks;
  }

  return Utils.flattenArray([
    split.before,
    insert,
    [{ kind: TextChunkKind.Control, selection: TextSelectionKind.Anchor }],
    split.after
  ]);
}
